import { useEffect, useState } from "react";
import {
  ArrowRight,
  CalendarCheck,
  Camera,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  X
} from "lucide-react";
import { extractedSite } from "./data/extractedSite";

type Page = (typeof extractedSite.pages)[number];
type Section = {
  heading: string;
  body: string;
  items: readonly string[];
  paragraphs: readonly string[];
  listLike: boolean;
  key: string;
};

type SitePage = Omit<Page, "route" | "title" | "description" | "category" | "sections" | "blocks" | "images"> & {
  route: string;
  title: string;
  description: string;
  category: string;
  sections: readonly Section[];
  contentSections: readonly Section[];
  blocks: readonly string[];
  images: readonly string[];
  lead: string;
  heroImage: string;
  displayTitle: string;
  documentTitle: string;
};

type IconComponent = typeof ShieldCheck;

const assets = {
  logo: "/assets/squarespace/logo.mint-oscuro-aad6c78a2c.png",
  hero: "/assets/squarespace/dsc04954-b0e067497f.jpg",
  about: "/assets/squarespace/img_0008-f150fc65df.jpg",
  consultation: "/assets/squarespace/img_0008-48468655c1.jpg",
  clinic: "/assets/squarespace/consulta_sensabell_madrid-1030x690-7f6d82626f.png"
};

const navigationItems = [
  { label: "Doctora Masa", to: "/cirujano-plastico" },
  { label: "Tratamientos", to: "/cirugia-mamaria-madrid" },
  { label: "Consulta", to: "/consulta" },
  { label: "Contacto", to: "/contacto-1" }
] as const;

const titleOverrides = new Map([
  ["/", "Cirugía plástica y estética en Madrid | Doctora Masa"],
  ["/cirujano-plastico", "Dra. Inmaculada Masa, cirujana plástica en Madrid | Doctora Masa"],
  ["/reconstruccion-mama-madrid", "Reconstrucción mamaria en Madrid | Doctora Masa"],
  ["/contacto-1", "Contacto y consulta en Madrid | Doctora Masa"],
  ["/consulta", "Consulta de cirugía plástica y estética en Madrid | Doctora Masa"],
  ["/pide-cita", "Pide cita con la Doctora Masa en Madrid"]
]);

const rawTreatmentGroups = [
  {
    title: "Mamas",
    routes: [
      "/aumento-de-pecho-madrid",
      "/elevacion-de-pecho-madrid",
      "/reduccion-de-pecho-madrid",
      "/asimetria-de-pecho-madrid",
      "/aumento-de-pecho-con-grasa",
      "/reconstruccion-mama-madrid"
    ]
  },
  {
    title: "Facial",
    routes: ["/blefaroplastia-madrid", "/rinoplastia-madrid", "/otoplastia-madrid", "/lifting-facial-madrid"]
  },
  {
    title: "Corporal",
    routes: [
      "/liposuccion-madrid",
      "/abdominoplastia-madrid",
      "/lifting-de-muslos",
      "/lipotransferencia-madrid",
      "/ciruga-intima-labioplastia-madrid"
    ]
  },
  {
    title: "Reconstrucción",
    routes: ["/cirugia-reconstructiva-madrid", "/reconstruccion-mama-madrid"]
  }
];

const principles: readonly { title: string; text: string; icon: IconComponent }[] = [
  {
    title: "Profesionalidad",
    text: "Experiencia hospitalaria y práctica estética privada con atención cercana y honesta.",
    icon: ShieldCheck
  },
  {
    title: "1ª consulta informativa",
    text: "Valoración personalizada para resolver dudas y revisar expectativas antes de decidir.",
    icon: CalendarCheck
  },
  {
    title: "Seguridad",
    text: "Plan quirúrgico adaptado a cada paciente, con seguimiento antes y después de la intervención.",
    icon: ShieldCheck
  }
];

const consultationSteps = [
  "Escuchamos tus necesidades y resolvemos tus dudas.",
  "Evaluamos tu caso y te explicamos las opciones más adecuadas.",
  "Te acompañamos en la toma de decisiones con claridad y confianza."
] as const;

function normalizeRoute(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/$/, "");
}

function splitBody(body: string) {
  return body.split("\n").map((item) => item.trim()).filter(Boolean);
}

function isListLike(items: readonly string[]) {
  if (items.length < 4) return false;
  return items.every((item) => item.length < 180);
}

function normalizeSection(section: { heading: string; body: string; items: readonly string[] }, index: number): Section {
  const body = section.body || "";
  const items = section.items.filter(Boolean);
  return {
    heading: section.heading || "",
    body,
    items,
    paragraphs: splitBody(body),
    listLike: isListLike(items),
    key: `${section.heading}-${index}`
  };
}

function getDocumentTitle(route: string, title: string, category: string) {
  const override = titleOverrides.get(route);
  if (override) return override;

  const cleanTitle = title.replace(/^LP - /, "");
  if (category === "procedure" && !/madrid/i.test(cleanTitle)) {
    return `${cleanTitle} en Madrid | Doctora Masa`;
  }

  return cleanTitle !== extractedSite.site.title ? `${cleanTitle} | Doctora Masa` : "Doctora Masa";
}

function blockToSection(block: string, index: number): Section {
  return normalizeSection({ heading: "", body: block, items: [block] }, index);
}

function normalizePage(page: Page): SitePage {
  const sections = page.sections.map(normalizeSection);
  const contentSections = sections.length ? sections : page.blocks.map(blockToSection);
  const title = page.title || extractedSite.site.title;
  return {
    ...page,
    route: page.route,
    title,
    description: page.description || "",
    category: page.category || "",
    sections,
    contentSections,
    blocks: page.blocks,
    images: page.images,
    lead: page.description || sections[0]?.paragraphs[0] || page.blocks[0] || "",
    heroImage: page.images.find((item) => !item.includes("logo")) || assets.hero,
    displayTitle: title.replace(/^LP - /, ""),
    documentTitle: getDocumentTitle(page.route, title, page.category || "")
  };
}

const pages = extractedSite.pages.map(normalizePage);
const pageByRoute = new Map<string, SitePage>(pages.map((page) => [page.route, page]));
const doctorHighlights = pageByRoute.get("/cirujano-plastico")?.sections[0]?.items.slice(0, 3) ?? [];
const appointmentCopy = pageByRoute.get("/pide-cita")?.sections[0]?.body || "Elige la consulta que prefieras y solicita tu cita.";
const treatmentGroups = rawTreatmentGroups
  .map((group) => ({
    title: group.title,
    links: group.routes.flatMap((route) => {
      const page = pageByRoute.get(route);
      return page ? [{ route, title: page.displayTitle }] : [];
    })
  }))
  .filter((group) => group.links.length);

function getPage(route: string) {
  return pageByRoute.get(route);
}

function Link({
  to,
  children,
  className,
  onNavigate
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  onNavigate?: () => void;
}) {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState({}, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
    onNavigate?.();
  };

  return (
    <a href={to} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

function Header({ route }: { route: string }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="site-header">
      <Link to="/" className="brand" onNavigate={() => setOpen(false)}>
        <img src={assets.logo} alt="" decoding="async" />
        <span>Doctora Masa</span>
      </Link>

      <nav className="desktop-nav" aria-label="Principal">
        {navigationItems.map((item) => (
          <Link key={item.to} to={item.to} className={route === item.to ? "active" : undefined}>
            {item.label}
          </Link>
        ))}
      </nav>

      <button className="menu-button" type="button" onClick={() => setOpen((value) => !value)} aria-label="Abrir navegación">
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {open ? (
        <nav className="mobile-nav" aria-label="Principal móvil">
          {navigationItems.map((item) => (
            <Link key={item.to} to={item.to} onNavigate={() => setOpen(false)}>
              {item.label}
            </Link>
          ))}
        </nav>
      ) : null}
    </header>
  );
}

function TreatmentLinks({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "treatment-links compact" : "treatment-links"}>
      {treatmentGroups.map((group) => (
        <section key={group.title} className="treatment-column">
          <h3>{group.title}</h3>
          <div className="rule" />
          <ul>
            {group.links.map((item) => {
              return (
                <li key={item.route}>
                  <Link to={item.route}>
                    <span>{item.title}</span>
                    <ArrowRight size={15} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function SectionContent({ section }: { section: Section }) {
  return (
    <section className="content-section">
      {section.heading ? <h2>{section.heading}</h2> : null}
      {section.listLike ? (
        <ul className="clean-list">
          {section.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        section.paragraphs.map((item) => <p key={item}>{item}</p>)
      )}
    </section>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero" id="inicio">
        <div className="hero-copy">
          <h1>Cirugía Plástica, Reparadora y Estética en Madrid</h1>
          <p>
            La doctora Inmaculada Masa Jurado es especialista en cirugía plástica, reparadora y estética en Madrid y miembro
            de SECPRE.
          </p>
          <div className="actions">
            <Link to="/pide-cita" className="button primary">
              Pide cita
            </Link>
            <Link to="/cirugia-mamaria-madrid" className="button secondary">
              Ver tratamientos
            </Link>
          </div>
        </div>
        <figure className="hero-media">
          <img src={assets.hero} alt="Dra. Inmaculada Masa Jurado" fetchPriority="high" decoding="async" />
        </figure>
      </section>

      <section className="section-block treatments" id="tratamientos">
        <div className="section-heading centered">
          <h2>Tratamientos</h2>
          <p>Rutas claras hacia las áreas principales de la web actual, conservando las páginas específicas existentes.</p>
        </div>
        <TreatmentLinks />
      </section>

      <section className="split-section mint">
        <div className="split-copy">
          <h2>Experiencia y trato personalizado</h2>
          {doctorHighlights.map((item) => <p key={item}>{item}</p>)}
          <Link to="/cirujano-plastico" className="button secondary">
            Conoce a la Doctora Masa
          </Link>
        </div>
        <figure className="split-media">
          <img src={assets.about} alt="Consulta con la Dra. Masa" loading="lazy" decoding="async" />
        </figure>
      </section>

      <section className="section-block principles">
        {principles.map(({ title, text, icon: Icon }) => (
          <article key={title} className="principle">
            <Icon size={26} aria-hidden="true" />
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </section>

      <section className="consultation-section">
        <div className="section-heading centered">
          <h2>Primera consulta informativa</h2>
          <p>{appointmentCopy}</p>
        </div>
        <div className="consultation-steps">
          {consultationSteps.map((step, index) => (
            <div className="step" key={step}>
              <strong>{index + 1}.</strong>
              <p>{step}</p>
            </div>
          ))}
        </div>
        <Link to="/pide-cita" className="button primary">
          Pide cita
        </Link>
      </section>

      <ContactBand />
    </>
  );
}

function PageHero({ page }: { page: SitePage }) {
  return (
    <section className="page-hero">
      <div>
        <h1>{page.title}</h1>
        <p>{page.lead}</p>
      </div>
      <figure>
        <img src={page.heroImage} alt="" fetchPriority="high" decoding="async" />
      </figure>
    </section>
  );
}

function DetailPage({ page }: { page: SitePage }) {
  const showTreatmentRail = page.category === "procedure";

  return (
    <>
      <PageHero page={page} />
      <main className={showTreatmentRail ? "detail-layout with-rail" : "detail-layout"}>
        {showTreatmentRail ? (
          <aside className="detail-rail" aria-label="Tratamientos">
            <h2>Tratamientos</h2>
            <TreatmentLinks compact />
          </aside>
        ) : null}

        <article className="article-body">
          {page.contentSections.length ? (
            page.contentSections.map((section) => <SectionContent key={section.key} section={section} />)
          ) : (
            <section className="content-section">
              <h2>{page.title}</h2>
              <p>Esta ruta está preservada desde la web original.</p>
            </section>
          )}
        </article>
      </main>
      {page.category === "legal" ? null : <ContactBand />}
    </>
  );
}

function ContactBand() {
  return (
    <section className="contact-band" id="contacto">
      <div>
        <h2>Doctora Masa</h2>
        <p>{extractedSite.site.tagline}</p>
      </div>
      <a href="https://maps.google.com/?q=Calle%20N%C3%BA%C3%B1ez%20de%20Balboa%20107%2C%20Madrid" target="_blank" rel="noreferrer">
        <MapPin size={20} />
        Calle Núñez de Balboa 107, Madrid
      </a>
      <a href="mailto:consulta@doctoramasa.com">
        <Mail size={20} />
        consulta@doctoramasa.com
      </a>
      <a href="tel:+34618133435">
        <Phone size={20} />
        +34 618 13 34 35
      </a>
      <a href={extractedSite.site.instagram} target="_blank" rel="noreferrer">
        <Camera size={20} />
        Instagram
      </a>
    </section>
  );
}

function NotFound() {
  return (
    <section className="not-found">
      <h1>Página no encontrada</h1>
      <p>La ruta solicitada no forma parte del mapa extraído desde la web original.</p>
      <Link to="/" className="button primary">
        Volver al inicio
      </Link>
    </section>
  );
}

export default function App({ initialRoute }: { initialRoute?: string } = {}) {
  const [route, setRoute] = useState(() =>
    normalizeRoute(initialRoute ?? (typeof window === "undefined" ? "/" : window.location.pathname))
  );
  const page = getPage(route);

  useEffect(() => {
    const syncRoute = () => setRoute(normalizeRoute(window.location.pathname));
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  useEffect(() => {
    document.title = page?.documentTitle ?? "Doctora Masa";
    document.documentElement.lang = extractedSite.site.language;
  }, [page]);

  return (
    <div>
      <Header route={route} />
      {route === "/" ? <HomePage /> : page ? <DetailPage page={page} /> : <NotFound />}
      <a className="whatsapp" href="https://wa.me/34618133435" aria-label="Contactar por WhatsApp" target="_blank" rel="noreferrer">
        <MessageCircle size={24} />
      </a>
    </div>
  );
}
